import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography,Snackbar,Alert, Avatar, IconButton, Tooltip, Link, Chip, Menu, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import CircularScore from "./CircularScore";

function RecommendationBadge({ value }) {
  const rec = (value || "").toUpperCase().replace(/_/g, "");
  let label = "—";
  let color = "#757575";
  let icon = null;

  if (rec === "STRONGSHORTLIST" || rec === "SHORTLIST") {
    label = "Shortlist";
    color = "#2e7d32"; // Green
    icon = <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />;
  } else if (rec === "REJECT") {
    label = "Reject";
    color = "#d32f2f"; // Red
    icon = <CancelIcon sx={{ fontSize: 16, mr: 0.5 }} />;
  } else if (rec === "REVIEW") {
    label = "Review";
    color = "#ed6c02"; // Orange
    icon = <InfoIcon sx={{ fontSize: 16, mr: 0.5 }} />;
  }

  if (label === "—") return <Typography color="text.disabled" variant="body2">—</Typography>;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        color: color,
        fontWeight: 600,
        fontSize: "0.875rem",
      }}
    >
      {rec === "STRONGSHORTLIST" || rec === "SHORTLIST" ? "✅ " : rec === "REJECT" ? "❌ " : rec === "REVIEW" ? "🟠 " : ""}
      {label}
    </Box>
  );
}

function MatchBadge({ value }) {
  const num = value != null ? Math.round(Number(value)) : null;
  const displayValue = num != null ? `${num}%` : "0%";
  const color = num >= 80 ? "#2e7d32" : num >= 50 ? "#ed6c02" : "#d32f2f";
  const bgColor = num >= 80 ? "#e8f5e9" : num >= 50 ? "#fff3e0" : "#ffebee";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 48,
        height: 48,
        borderRadius: "12px",
        bgcolor: bgColor,
        color: color,
        fontWeight: 800,
        fontSize: "0.85rem",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      }}
    >
      {displayValue}
    </Box>
  );
}

function MissingSkillsCell({ skills = [] }) {
  if (!skills || skills.length === 0) return <Typography variant="body2" color="text.secondary">None</Typography>;

  const stripHtml = (html) => {
    if (typeof html !== 'string') return html;
    return html.replace(/<[^>]*>?/gm, '').trim();
  };

  const displayLimit = 3;
  const displaySkills = skills.slice(0, displayLimit);
  const remainingCount = skills.length - displayLimit;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {displaySkills.map((skill, index) => (
        <Chip
          key={index}
          label={stripHtml(skill)}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 500,
            borderRadius: "6px",
            bgcolor: "#f1f3f4",
            color: "#5f6368",
            border: "1px solid #dadce0",
          }}
        />
      ))}
      {remainingCount > 0 && (
        <Tooltip
          arrow
          placement="top"
          title={
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: 0.5 }}>
              {skills.slice(displayLimit).map((skill, index) => (
                <Chip
                  key={index}
                  label={stripHtml(skill)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    borderRadius: "6px",
                    bgcolor: "#f1f3f4",
                    color: "#5f6368",
                    border: "1px solid #dadce0",
                  }}
                />
              ))}
            </Box>
          }
        >
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "transparent",
              color: "#008b8b",
              border: "1px dashed #008b8b",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

export default function CandidateTable({ candidates = [], onViewResume, loading = false }) {
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
useEffect(() => {
  setPaginationModel((prev) => ({ ...prev, page: 0 }));
}, [candidates]);
  const rows = useMemo(() => {
    if (!candidates || !Array.isArray(candidates)) return [];
    return candidates.map((c) => {

      return {
        id: c.application_id || c.id || `${c.candidate_id || "unknown"}-${c.job_id || "unknown"}`,
        ...c,
        match_score: c.match_score == null ? null : Number(c.match_score),
      };
    });
  }, [candidates]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Candidate",
        width: 280,
        sortable: false,
        valueGetter: (value, row) => {
          const first = row?.first_name || "";
          const last = row?.last_name || "";
          const full = `${first} ${last}`.trim();
          return full || "(No name)";
        },
        renderCell: (params) => {
          const name = params.value;
          const initials = name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
          const email = params.row?.email || "";

          const colors = ["#e0f2f1", "#f3e5f5", "#fff3e0", "#e8eaf6", "#fce4ec"];
          const bgColor = colors[name.length % colors.length];
          const textColor = "#00695c";

          return (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ height: "100%" }}>
              <Avatar
                sx={{
                  bgcolor: bgColor,
                  color: textColor,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  width: 40,
                  height: 40,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ overflow: "hidden", flex: 1 }}>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.2, color: "text.primary" }}>
                  {name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                 <Tooltip title={email} arrow placement="top">
                   <Typography
                     variant="body2"
                     color="text.secondary"
                     sx={{
                       lineHeight: 1.2,
                       fontSize: "0.75rem",
                       flex: 1,
                       minWidth: 0,
                       maxWidth: "180px",
                       overflow: "hidden",
                       textOverflow: "ellipsis",
                       whiteSpace: "nowrap",
                       display: "block",
                       cursor: "default",
                     }}
                   >
                     {email}
                   </Typography>
                 </Tooltip>

                  {email && (
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        minWidth: 20,
                      }}
                    >
                     <>
                       {copiedEmailId === params.row.id && (
                         <Box
                           sx={{
                             position: "absolute",
                             top: -38,
                             left: "50%",
                             transform: "translateX(-50%)",
                             bgcolor: "#008b8b",
                             color: "#fff",
                             px: 1.2,
                             py: 0.5,
                             borderRadius: "8px",
                             fontSize: "0.72rem",
                             fontWeight: 600,
                             whiteSpace: "nowrap",
                             zIndex: 10,
                             boxShadow: "0 2px 8px rgba(0,0,0,0.15)",

                             "&::after": {
                               content: '""',
                               position: "absolute",
                               bottom: -6,
                               left: "50%",
                               transform: "translateX(-50%)",
                               borderWidth: "6px 6px 0 6px",
                               borderStyle: "solid",
                               borderColor: "#008b8b transparent transparent transparent",
                             },
                           }}
                         >
                           Copied!
                         </Box>
                       )}

                       <Tooltip title="Copy Email" arrow placement="top">
                         <IconButton
                           size="small"
                           onClick={(e) => {
                             e.stopPropagation();

                             navigator.clipboard.writeText(email);

                             setCopiedEmailId(params.row.id);

                             setTimeout(() => {
                               setCopiedEmailId(null);
                             }, 1500);
                           }}
                           sx={{
                             p: 0.4,
                             color:
                               copiedEmailId === params.row.id
                                 ? "#008b8b"
                                 : "#9e9e9e",
                             flexShrink: 0,
                             ml: 0.5,

                             "&:hover": {
                               bgcolor: "#f5f5f5",
                               color: "#008b8b",
                             },
                           }}
                         >
                           <ContentCopyIcon sx={{ fontSize: 16 }} />
                         </IconButton>
                       </Tooltip>
                     </>
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>
          );
        },
      },
      {
        field: "match_score",
        headerName: "Score",
        width: 100,
        align: "center",
        headerAlign: "center",
        type: "number",
        renderCell: (params) => <CircularScore score={params.value} size={40} />,
        sortComparator: (v1, v2) => {
          const a = Number.isFinite(v1) ? v1 : -1;
          const b = Number.isFinite(v2) ? v2 : -1;
          return a - b;
        },
      },
      {
        field: "skill_match",
        headerName: "Skill Match",
        width: 100,
        align: "center",
        headerAlign: "center",
        type: "number",
        valueGetter: (value, row) => row?.skill_match ?? null,
        renderCell: (params) => <MatchBadge value={params.value} />,
      },
      {
        field: "experience_match",
        headerName: "Exp Match",
        width: 100,
        align: "center",
        headerAlign: "center",
        type: "number",
        valueGetter: (value, row) => row?.experience_match ?? null,
        renderCell: (params) => <MatchBadge value={params.value} />,
      },
      {
        field: "recommendation",
        headerName: "Recommendation",
        width: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => <RecommendationBadge value={params.value} />,
      },
      {
        field: "missing_skills",
        headerName: "Missing Skills",
        minWidth: 200,
        flex: 1,
        valueGetter: (value, row) => {
          let missing = row?.missing_skills || [];
          if (typeof missing === "string") {
            try {
              missing = JSON.parse(missing);
            } catch (e) {
              missing = [];
            }
          }
          return Array.isArray(missing) ? missing : [];
        },
        renderCell: (params) => <MissingSkillsCell skills={params.value} />,
      },
      {
        field: "applied_at",
        headerName: "Applied Date",
        width: 130,
        valueGetter: (value, row) => row?.applied_at || row?.created_at || "",
        renderCell: (params) => {
          const v = params.value;
          if (!v) return <Typography color="text.disabled">—</Typography>;
          const d = new Date(v);
          if (Number.isNaN(d.getTime())) return <Typography color="text.disabled">—</Typography>;

          const dateStr = d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          const timeStr = d.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "text.primary" }}>
                {dateStr}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                {timeStr}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => <ActionButtons row={params.row} onViewResume={onViewResume} />,
      },
    ],
    [onViewResume]
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 0,
        boxShadow: "0 8px 20px rgba(79,70,229,0.12)",
        border: "1px solid #4F46E5",
        overflow: "hidden",
        minHeight: 700,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={90}
          disableRowSelectionOnClick
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          autoHeight
          initialState={{
            sorting: { sortModel: [{ field: "match_score", sort: "desc" }] },
          }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#fff",
              borderBottom: "1px solid #4F46E5",
              borderColor: "divider",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                color: "text.secondary",
              },
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                bgcolor: "#f9fbfb",
              },
            },
            "& .MuiDataGrid-cell": {
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              py: 1,
              "&:focus": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #4F46E5",
            },
          }}
        />
      </Box>
      <Box sx={{ p: 2, borderTop: "1px solid #4F46E5", display: "flex", gap: 3 }}>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Recommendation:
        </Typography>
        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
            <Typography variant="caption">Shortlist</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <InfoIcon sx={{ fontSize: 14, color: "warning.main" }} />
            <Typography variant="caption">Review</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CancelIcon sx={{ fontSize: 14, color: "error.main" }} />
            <Typography variant="caption">Reject</Typography>
          </Stack>
        </Stack>
            </Box>



          </Box>
        );
      }

function ActionButtons({ row, onViewResume }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        variant="outlined"
        size="small"
        startIcon={<VisibilityIcon />}
        onClick={() => onViewResume && onViewResume(row)}
        disabled={!(row?.resume_url || row?.resume || row?.resume_key)}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          borderColor: "#008b8b",
          color: "#008b8b",
          px: 1.5,
          "&:hover": {
            borderColor: "#007a7a",
            bgcolor: "rgba(0, 139, 139, 0.04)",
          },
        }}
      >
        View Resume
      </Button>
      <IconButton size="small" onClick={handleClick}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleClose}>Email Candidate</MenuItem>
        <MenuItem onClick={handleClose}>Move to Next Stage</MenuItem>
        <MenuItem onClick={handleClose} sx={{ color: "error.main" }}>Reject</MenuItem>
      </Menu>
    </Stack>
  );
}
